import { type Cents, type DepositAccount } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    ChevronRightIcon,
    ControlledInput,
    FootnoteRegular,
    H2Emphasized,
    Icon,
    InfoButton,
    InfoRow,
    Notice,
    RadioInputRow,
    RowList,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
    MINIMUM_TRANSFER_AMOUNT_CENTS,
} from '../../../../common/utils/bankingUtils';
import { getNonTerminatedCards } from '../../../../common/utils/cardUtils';
import {
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../../common/utils/dateUtils';
import { type BankAccountDisplayDetail } from '../../../types/BankAccount';

export enum PaymentSelection {
    PriorBalance = 'priorBalance',
    OutstandingBalance = 'outstandingBalance',
    CustomAmount = 'customAmount',
}

const paymentSchema = z
    .object({
        paymentSelection: z.enum([
            PaymentSelection.CustomAmount,
            PaymentSelection.OutstandingBalance,
            PaymentSelection.PriorBalance,
        ]),
        customAmount: z.string(),
    })
    .refine(
        ({ paymentSelection, customAmount }) =>
            paymentSelection !== PaymentSelection.CustomAmount ||
            !isEmpty(customAmount),
        {
            message: 'Please enter an amount',
            path: ['customAmount'],
        },
    );

type PaymentSchema = z.infer<typeof paymentSchema>;

interface PaymentOptionsProps {
    balanceDue: string;
    cardBalance: string;
    primaryButtonText: string;
    onPress: (
        paymentSelection: PaymentSelection,
        paymentAmount: number,
    ) => void;
    onPressUseExternalAccount: (
        paymentSelection: PaymentSelection,
        paymentAmount: number,
    ) => void;
    includeExternalPaymentsButton: boolean;
    selectedPaymentOption: PaymentSelection;
}

const PaymentOptions = ({
    balanceDue,
    cardBalance,
    primaryButtonText,
    includeExternalPaymentsButton,
    selectedPaymentOption,
    onPress,
    onPressUseExternalAccount,
}: PaymentOptionsProps) => {
    const {
        register,
        control,
        watch,
        handleSubmit,
        formState: { errors },
        clearErrors,
    } = useForm<PaymentSchema>({
        resolver: zodResolver(paymentSchema),
        defaultValues: {
            customAmount: '',
            paymentSelection: selectedPaymentOption,
        },
    });

    const showExternalPaymentButton = includeExternalPaymentsButton;

    const customAmount = watch('customAmount');
    const paymentSelection = watch('paymentSelection');

    const paymentSelectionToAmountCents = (
        paymentSelection,
        customAmount: string,
    ): Cents => {
        return formattedDollarsToCents(
            paymentSelection === PaymentSelection.PriorBalance
                ? balanceDue
                : paymentSelection === PaymentSelection.OutstandingBalance
                  ? cardBalance
                  : customAmount,
        );
    };

    const onSubmit: SubmitHandler<PaymentSchema> = ({
        paymentSelection,
        customAmount,
    }) => {
        const amountCents = paymentSelectionToAmountCents(
            paymentSelection,
            customAmount,
        );
        onPress(paymentSelection, amountCents);
    };

    const onSubmitPayWithExternalAccount: SubmitHandler<PaymentSchema> = ({
        paymentSelection,
        customAmount,
    }) => {
        const amountCents = paymentSelectionToAmountCents(
            paymentSelection,
            customAmount,
        );
        onPressUseExternalAccount(paymentSelection, amountCents);
    };

    const isCustomAmountOverOutstandingBalance =
        !isEmpty(customAmount) &&
        formattedDollarsToCents(customAmount) >
            formattedDollarsToCents(cardBalance);

    const customAmountBelowMinimum =
        isEmpty(customAmount) ||
        formattedDollarsToCents(customAmount) < MINIMUM_TRANSFER_AMOUNT_CENTS;

    const isReviewButtonDisabled =
        paymentSelection === PaymentSelection.CustomAmount &&
        (isCustomAmountOverOutstandingBalance || customAmountBelowMinimum);

    return (
        <YStack flexGrow={1} justifyContent="space-between">
            <View>
                <Controller
                    control={control}
                    name="paymentSelection"
                    render={({ field: { onChange, value } }) => (
                        <YStack>
                            {balanceDue != null && balanceDue !== '$0.00' && (
                                <RadioInputRow
                                    label={balanceDue}
                                    isSelected={
                                        value === PaymentSelection.PriorBalance
                                    }
                                    onPress={() => {
                                        clearErrors();
                                        onChange(PaymentSelection.PriorBalance);
                                    }}
                                    details="Prior statement balance"
                                />
                            )}
                            {cardBalance != null && cardBalance !== '$0.00' && (
                                <RadioInputRow
                                    label={cardBalance}
                                    isSelected={
                                        value ===
                                        PaymentSelection.OutstandingBalance
                                    }
                                    onPress={() => {
                                        clearErrors();
                                        onChange(
                                            PaymentSelection.OutstandingBalance,
                                        );
                                    }}
                                    details="Total outstanding balance"
                                />
                            )}
                            <RadioInputRow
                                label="Custom amount"
                                isSelected={
                                    value === PaymentSelection.CustomAmount
                                }
                                onPress={() => {
                                    onChange(PaymentSelection.CustomAmount);
                                }}
                            />
                            {paymentSelection ===
                                PaymentSelection.CustomAmount && (
                                <View marginLeft={12} marginTop={0} width={300}>
                                    {/* Note: this component is not rendered as a child of the CustomAmount option
                                        because it is part of the same react-hook-form. If we nest form components
                                        within each other, the form re-renders and wipes away state of the nested
                                        elements on each change. */}
                                    <ControlledInput
                                        editable={
                                            value ===
                                            PaymentSelection.CustomAmount
                                        }
                                        disabled={
                                            value !==
                                            PaymentSelection.CustomAmount
                                        }
                                        value={customAmount}
                                        marginVertical={12}
                                        type="numeric"
                                        formatValueOnChange={
                                            formatCurrencyInputOnChange
                                        }
                                        name="customAmount"
                                        placeholder="Enter an amount"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        enterKeyHint="done"
                                        onSubmitEditing={() => {
                                            Keyboard.dismiss();
                                        }}
                                    />
                                    {isCustomAmountOverOutstandingBalance && (
                                        <View
                                            marginTop={8}
                                            borderRadius={20}
                                            padding={16}
                                            backgroundColor="$red200"
                                        >
                                            <BodyMediumRegular>
                                                You can&apos;t make a payment
                                                larger than your total
                                                outstanding balance.
                                            </BodyMediumRegular>
                                        </View>
                                    )}
                                </View>
                            )}
                        </YStack>
                    )}
                />
            </View>

            {showExternalPaymentButton && (
                <View flexGrow={1} justifyContent="flex-end" marginBottom={16}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        disabled={isReviewButtonDisabled}
                        text="Or pay with an external account"
                        size={ButtonSize.LARGE}
                        onPress={handleSubmit(onSubmitPayWithExternalAccount)}
                    />
                </View>
            )}
            <Button
                variant={ButtonVariant.PRIMARY}
                text={primaryButtonText}
                size={ButtonSize.LARGE}
                onPress={handleSubmit(onSubmit)}
                disabled={isReviewButtonDisabled}
            />
        </YStack>
    );
};

interface MakePaymentProps {
    balanceDue: string;
    cardBalance: string;
    selectedPaymentOption: PaymentSelection;
    onPress: (
        paymentSelection: PaymentSelection,
        paymentAmount: number,
    ) => void;
    onPressUseExternalAccount: (
        paymentSelection: PaymentSelection,
        paymentAmount: number,
    ) => void;
}

export const MakePaymentTab = ({
    balanceDue,
    cardBalance,
    selectedPaymentOption,
    onPress,
    onPressUseExternalAccount,
}: MakePaymentProps) => {
    const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

    return (
        <YStack justifyContent="space-between">
            <ReservedFundsSheet
                isOpen={isSheetOpen}
                onClose={() => {
                    setIsSheetOpen(false);
                }}
            />
            <View flexGrow={1}>
                <BodyLargeEmphasized>
                    Pay with already reserved funds at Fruitful.
                </BodyLargeEmphasized>
                <XStack marginBottom={24}>
                    <InfoButton
                        text="Learn more"
                        onPress={() => {
                            setIsSheetOpen(true);
                        }}
                    />
                </XStack>

                <PaymentOptions
                    balanceDue={balanceDue}
                    cardBalance={cardBalance}
                    selectedPaymentOption={selectedPaymentOption}
                    onPress={(
                        paymentSelection: PaymentSelection,
                        paymentAmount: number,
                    ) => {
                        onPress(paymentSelection, paymentAmount);
                    }}
                    primaryButtonText="Review"
                    includeExternalPaymentsButton={true}
                    onPressUseExternalAccount={(
                        paymentSelection: PaymentSelection,
                        paymentAmount: number,
                    ) => {
                        onPressUseExternalAccount(
                            paymentSelection,
                            paymentAmount,
                        );
                    }}
                />
            </View>
        </YStack>
    );
};

interface ReviewPaymentTabProps {
    paymentAmount: number;
    account: BankAccountDisplayDetail;
    externalPaymentAccount?: DepositAccount;
    onPress: () => void;
}

export const ReviewPaymentTab = ({
    paymentAmount,
    account,
    externalPaymentAccount,
    onPress,
}: ReviewPaymentTabProps) => {
    const { nickname, accountNumberMasked, cards } = account;
    const lastFourDepositAccount = accountNumberMasked.slice(-4);

    const displayCards = getNonTerminatedCards(cards);
    const cardsLastFour = displayCards
        .map((card) => `*${card.lastFour}`)
        ?.join(', ');

    return (
        <YStack justifyContent="space-between">
            <YStack gap={12}>
                <RowList>
                    <InfoRow
                        title="Card"
                        value={`${nickname} Card (${cardsLastFour})`}
                    />
                    <InfoRow
                        title="From"
                        value={
                            externalPaymentAccount != null ? (
                                <View>
                                    <BodySmallRegular alignSelf="flex-end">
                                        {externalPaymentAccount.nickname}
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        alignSelf="flex-end"
                                        color="$gray1000"
                                    >
                                        {externalPaymentAccount.bankName} *
                                        {
                                            externalPaymentAccount.accountNumberMasked
                                        }
                                    </BodySmallRegular>
                                </View>
                            ) : (
                                <BodySmallRegular
                                    textAlign="right"
                                    maxWidth={280}
                                    numberOfLines={2}
                                >
                                    Reserved funds in {nickname} (*
                                    {lastFourDepositAccount})
                                </BodySmallRegular>
                            )
                        }
                    />
                    <InfoRow
                        title="Amount"
                        value={centsToFormattedDollars(paymentAmount)}
                    />
                    <InfoRow
                        title="Date"
                        value={formatWithoutTimezone(
                            new Date(),
                            SHORT_READABLE_DATE_FORMAT,
                        )}
                    />
                </RowList>
                {externalPaymentAccount != null ? (
                    <BodySmallRegular color="$gray1000">
                        Payments from external accounts will reflect in your
                        available balance immediately.
                    </BodySmallRegular>
                ) : (
                    <BodySmallRegular color="$gray1000">
                        By confirming below, I authorize Fruitful to use
                        Reserved funds as payment on my card balance as
                        indicated above.
                    </BodySmallRegular>
                )}
            </YStack>
            <View>
                {externalPaymentAccount != null && (
                    <FootnoteRegular color="$gray1100" marginBottom={16}>
                        By confirming below, I authorize Fruitful to initiate
                        the payment as stated above.
                    </FootnoteRegular>
                )}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text="Confirm and pay"
                    size={ButtonSize.LARGE}
                    onPress={onPress}
                />
            </View>
        </YStack>
    );
};

interface PaymentPostedTabProps {
    onPressDone: () => void;
    onPressSetUpAutoPay: () => void;
    autoPayEnabled?: boolean | null;
}

export const PaymentPostedTab = ({
    onPressDone,
    onPressSetUpAutoPay,
    autoPayEnabled,
}: PaymentPostedTabProps) => {
    return (
        <YStack
            justifyContent="space-between"
            height="100%"
            paddingTop={64}
            paddingHorizontal={24}
        >
            <YStack alignItems="center" gap={40}>
                <View
                    alignSelf="center"
                    padding={20}
                    borderRadius={20}
                    backgroundColor="$green900"
                >
                    <Icon icon={CheckIcon} color="$white" size={40} />
                </View>
                <H2Emphasized>Payment posted</H2Emphasized>
                {!autoPayEnabled && (
                    <Notice
                        variant="neutral"
                        title="Never miss a payment"
                        description="Avoid having to make payments manually by setting up AutoPay."
                        action={{
                            text: 'Set up AutoPay',
                            onPress: onPressSetUpAutoPay,
                            underlined: true,
                        }}
                    />
                )}
            </YStack>
            <Button
                variant={ButtonVariant.PRIMARY}
                text="Done"
                size={ButtonSize.LARGE}
                onPress={onPressDone}
            />
        </YStack>
    );
};

interface ReservedFundsSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

const ReservedFundsSheet = ({ isOpen, onClose }: ReservedFundsSheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Your reserved funds"
            showCloseButton
        >
            <YStack
                marginVertical={20}
                maxWidth={500}
                width="100%"
                alignSelf="center"
            >
                <BodyMediumRegular color="$gray1100">
                    This is the money already set aside from your Fruitful Cash
                    Account to cover the purchases you&apos;ve made with your
                    Fruitful Card.
                </BodyMediumRegular>
            </YStack>
        </Sheet>
    );
};

interface ExternalAccountPaymentProps {
    account: BankAccountDisplayDetail;
    externalPaymentAccount?: DepositAccount;
    onPressSelectAccount: () => void;
    onPressSelectAmount: () => void;
    onPressReview: () => void;
    paymentAmount: number;
}

export const ExternalAccountPayment = ({
    account,
    paymentAmount,
    externalPaymentAccount,
    onPressSelectAccount,
    onPressSelectAmount,
    onPressReview,
}: ExternalAccountPaymentProps) => {
    const { nickname, cards } = account;

    const displayCards = getNonTerminatedCards(cards);
    const cardsLastFour = displayCards
        .map((card) => `*${card.lastFour}`)
        ?.join(', ');

    return (
        <YStack justifyContent="space-between">
            <YStack gap={12}>
                <RowList>
                    <InfoRow
                        title="Card"
                        value={`${nickname} Card (${cardsLastFour})`}
                    />
                    <InfoRow
                        title="From"
                        value={
                            <YStack hoverStyle={{ cursor: 'pointer' }}>
                                {externalPaymentAccount?.nickname != null ? (
                                    <>
                                        <BodySmallRegular alignSelf="flex-end">
                                            {externalPaymentAccount.nickname}
                                        </BodySmallRegular>

                                        <BodySmallRegular
                                            alignSelf="flex-end"
                                            color="$gray1000"
                                        >
                                            {externalPaymentAccount.bankName} *
                                            {
                                                externalPaymentAccount.accountNumberMasked
                                            }
                                        </BodySmallRegular>
                                    </>
                                ) : (
                                    <BodySmallRegular>
                                        Choose account
                                    </BodySmallRegular>
                                )}
                            </YStack>
                        }
                        trailingIcon={ChevronRightIcon}
                        onPress={onPressSelectAccount}
                    />
                    <InfoRow
                        title="Amount"
                        value={centsToFormattedDollars(paymentAmount)}
                        trailingIcon={ChevronRightIcon}
                        onPress={onPressSelectAmount}
                    />
                    <InfoRow
                        title="Date"
                        value={formatWithoutTimezone(
                            new Date(),
                            SHORT_READABLE_DATE_FORMAT,
                        )}
                    />
                </RowList>
            </YStack>
            <Button
                variant={ButtonVariant.PRIMARY}
                text="Review"
                size={ButtonSize.LARGE}
                onPress={onPressReview}
                disabled={externalPaymentAccount == null || paymentAmount === 0}
            />
        </YStack>
    );
};

interface SelectPaymentAmountSheetProps {
    isAmountSheetOpen: boolean;
    balanceDue: string;
    cardBalance: string;
    selectedPaymentOption: PaymentSelection;
    onPress: (
        paymentSelection: PaymentSelection,
        paymentAmount: number,
    ) => void;
    onClose: () => void;
}

export const SelectPaymentAmountSheet = ({
    balanceDue,
    cardBalance,
    selectedPaymentOption,
    isAmountSheetOpen,
    onClose,
    onPress,
}: SelectPaymentAmountSheetProps) => {
    return (
        <Sheet
            isOpen={isAmountSheetOpen}
            onClose={onClose}
            title="Amount"
            showCloseButton
        >
            <YStack width="100%" maxWidth={400} alignSelf="center">
                <PaymentOptions
                    balanceDue={balanceDue}
                    cardBalance={cardBalance}
                    selectedPaymentOption={selectedPaymentOption}
                    primaryButtonText="Update amount"
                    onPress={(
                        paymentSelection: PaymentSelection,
                        paymentAmount: number,
                    ) => {
                        onPress(paymentSelection, paymentAmount);
                    }}
                    includeExternalPaymentsButton={false}
                    onPressUseExternalAccount={() => {}}
                />
            </YStack>
        </Sheet>
    );
};
